/** The audit log. Spec 11.4: "Filter by actor, action, entity, date." */
import type { AuditEntryDto } from '@lila/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Field } from '../../../components/ui/Field';
import { Pagination } from '../../../components/ui/Pagination';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminAudit } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';
import { useCursorPages } from '../../../lib/pagination';

export const Route = createFileRoute('/$lang/admin/audit')({ component: Page });

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);

  const [action, setAction] = useState('');
  const pages = useCursorPages();
  const audit = useAdminAudit({ action, cursor: pages.cursor });

  const columns: readonly Column<AuditEntryDto>[] = [
    {
      id: 'when',
      header: t('admin:audit.when'),
      cell: (row) => new Date(row.createdAt).toLocaleString(i18n.language),
    },
    { id: 'action', header: t('admin:audit.action'), cell: (row) => row.action },
    {
      id: 'actor',
      header: t('admin:audit.actor'),
      hideOnMobile: true,
      /* A null actor is the system — a job, or a webhook. Saying so beats an empty cell. */
      cell: (row) => row.actorUserId ?? t('admin:audit.system'),
    },
    {
      id: 'entity',
      header: t('admin:audit.entity'),
      hideOnMobile: true,
      cell: (row) =>
        `${row.entityType}${row.entityId === null ? '' : ` · ${row.entityId.slice(0, 8)}`}`,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.audit')}</h1>

      <div className="max-w-md">
        <Field
          label={t('admin:audit.action')}
          hint="admin.users.suspend"
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
            pages.reset();
          }}
        />
      </div>

      {audit.error !== null && <Alert tone="error">{t(errorKeyFor(audit.error))}</Alert>}

      {audit.isLoading ? (
        <SkeletonList rows={6} label={t('admin:nav.audit')} />
      ) : (
        <>
          <DataTable
            caption={t('admin:nav.audit')}
            columns={columns}
            rows={audit.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={<EmptyState title={t('admin:audit.empty')} />}
          />
          <Pagination
            hasPrevious={pages.hasPrevious}
            hasNext={(audit.data?.nextCursor ?? null) !== null}
            onPrevious={pages.previous}
            onNext={() => {
              pages.next(audit.data?.nextCursor ?? null);
            }}
            labels={{
              previous: t('common:pagination.previous'),
              next: t('common:pagination.next'),
              nav: t('admin:nav.audit'),
            }}
          />
        </>
      )}
    </div>
  );
}
